import CalendarItem from 'owa-service/lib/contract/CalendarItem';
import CalendarItemNotificationPayloadExtended from './CalendarItemNotificationPayloadExtended';
import { trace } from 'owa-trace';

/**
 * A queue that stores incoming notifications
 */
export default class NotificationsQueue {
    // A queue that stores incoming notifications
    private notificationsQueue: CalendarItemNotificationPayloadExtended[];

    // instanceKeyToUid stores a mapping between instance keys and UIDs of the notifications in the queue.
    private instanceKeyToUid: { [InstanceKey: string]: string } = {};

    constructor() {
        this.notificationsQueue = [];
        this.instanceKeyToUid = {};
    }

    private set(notification: CalendarItemNotificationPayloadExtended) {
        this.notificationsQueue.push(notification);
    }

    private clear() {
        this.notificationsQueue = [];
    }

    private getlength() {
        return this.notificationsQueue.length;
    }

    private getInstanceKeyToUid(instanceKey: string) {
        return this.instanceKeyToUid[instanceKey];
    }

    private setInstanceKeyToUid(instanceKey: string, uid: string) {
        this.instanceKeyToUid[instanceKey] = uid;
    }

    private deleteInstanceKeyToUid(instanceKey: string) {
        delete this.instanceKeyToUid[instanceKey];
    }

    processQueue(handler: (notification: CalendarItemNotificationPayloadExtended) => void) {
        this.notificationsQueue.map(handler);
        this.clear();
    }

    enqueueNotification(notification: CalendarItemNotificationPayloadExtended) {
        // If the notification is for a reload, we can remove everything from the cache, because there is no point
        // in processing individual notifications if we are just going to end up reloading everything anyways.
        if (notification.isReloadNotification()) {
            trace.info(
                '[CalendarNotifications]: A reload notification will be enqueued. All existing notifications will be discarded.'
            );
            this.clear();
            this.set(notification);
        } else {
            // The first thing that we need to do is see if there is already a notification for that same item.
            // If there is one, we'll remove it so we only process the most recent one.
            const notificationItem = notification.NotificationPayload.Item;
            const isNotificationItemInstanceKeyPresent = !!notificationItem.InstanceKey;
            const valueForComparisonFromNotification = isNotificationItemInstanceKeyPresent
                ? notificationItem.InstanceKey
                : notificationItem.ItemId.Id;
            for (let i = this.getlength() - 1; i >= 0; i--) {
                // If the InstanceKey is present (for the case of server-generated notifications), we'll use it
                // for the comparison. If it isn't (offline notifications), we'll use the id.
                const calItemInQueue: CalendarItem = this.notificationsQueue[i].NotificationPayload
                    .Item;
                let valueForComparisonFromQueue: string;
                let traceSuffix: string;

                if (isNotificationItemInstanceKeyPresent) {
                    valueForComparisonFromQueue = calItemInQueue.InstanceKey;
                    traceSuffix = 'InstanceKey';
                } else {
                    valueForComparisonFromQueue = calItemInQueue.ItemId.Id;
                    traceSuffix = 'ItemId';
                }

                if (valueForComparisonFromNotification === valueForComparisonFromQueue) {
                    if (!!this.notificationsQueue[i].InstanceKeyToDelete) {
                        notification.InstanceKeyToDelete = this.notificationsQueue[
                            i
                        ].InstanceKeyToDelete;
                    }
                    trace.info(
                        `[CalendarNotifications]: Found an existing notification in the queue for the same ${traceSuffix}. Old notification will be removed.`
                    );
                    this.notificationsQueue.splice(i, 1);

                    // We don't need to keep looking because we can never have more than one notification for the
                    // same item in the queue (the code currently being executed guarantees this).
                    break;
                }
            }

            // There is a special case that we need to handle. When the user accepts (or marks as tentative) a recurring meeting via Outlook,
            // this is what happens:
            // 1) The server issues 4 notifications - RowModified, RowDeleted, RowAdded, RowModified.
            // 2) The calendar item is cloned and marked with the user's response (this generates a RowAdded notification for the cloned item with
            //    a different InstanceKey, since it is a new item in the store, but with the same UID, because conceptually it is the same meeting).
            // 3) The old calendar item is deleted. One caveat here is that RowDeleted only has the InstanceKey and doesn't have the UID.

            // RowDeleted notification gets processed first and then the RowAdded notification, due to which, the series disappears from the surface for a while
            // and then re-appears. To avoid flickering, we delete the old items only when we are ready to add the newly expanded series. Here's how
            // we'll achieve this:
            // a) When we receive a RowModified notification for a recurring item (which always precedes the RowDeleted), we'll store a mapping between
            //    InstanceKey -> UID. This will be useful to get the UID of RowDeleted notification later.
            // b) When the notification for RowAdded comes in, we scan the queue for a RowDeleted notification having the same UID.
            //    If we find it, we can combine them into one, which will do both the delete of the old series and the add for the new one,
            //    and discard the delete notification. Since the two operations will happen right after the other, there should be no visible flickering.
            const { NotificationPayload } = notification;
            const notificationItemInstanceKey = NotificationPayload.Item.InstanceKey;
            if (!!notificationItemInstanceKey && notification.isRecurringMaster()) {
                const notificationItemUID = NotificationPayload.Item.UID;
                if (notification.isRowModifiedNotification()) {
                    // If the notification has a UID, add it to the mapping (see (a) in the comments above)
                    !!notificationItemUID &&
                        this.setInstanceKeyToUid(notificationItemInstanceKey, notificationItemUID);
                } else if (!!notificationItemUID && notification.isRowAddedNotification()) {
                    // Now we'll do another pass at the list, looking for a RowDeleted notification that we can merge with (step (b) above)
                    for (let i = this.getlength() - 1; i >= 0; i--) {
                        if (this.notificationsQueue[i].isRowDeletedNotification()) {
                            const queuedItemInstanceKey = this.notificationsQueue[i]
                                .NotificationPayload.Item.InstanceKey;
                            // Get the UID corresponding to this instance key
                            const uidInQueue = this.getInstanceKeyToUid(queuedItemInstanceKey);
                            // And now that we used it, we can remove it from the dictionary
                            this.deleteInstanceKeyToUid(queuedItemInstanceKey);
                            // We'll do the comparison based on UID this time.
                            if (uidInQueue === notificationItemUID) {
                                trace.info(
                                    '[CalendarNotifications]: "Found an existing RowDeleted notification in the queue for the same UID, which will be merged with the RowAdded.'
                                );
                                notification.InstanceKeyToDelete = queuedItemInstanceKey;
                                this.notificationsQueue.splice(i, 1);
                                break;
                            }
                        }
                    }
                }
            }
            // Now that we removed redundant notifications, we'll add the new one
            this.set(notification);
        }
    }
}
